import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { useAgreement } from "../../../context/AgreementContext";
import { useJob } from "../../../context/JobContext";
import { useUserId, useUserRole, cleanJobDescription, formatDateString } from "../../../util_functions/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import {
  FaUserTie,
  FaClipboardList,
  FaRegCalendarAlt,
  FaDollarSign,
  FaFileAlt,
} from "react-icons/fa";
import { LuClipboardList } from "react-icons/lu";
import AgreementSignedSuccessModal from "./AgreementSignedSuccessModal";
import config from "../../../config/config";
import axios from "axios";

const AgreementDetails = () => {
  const { workerUser, clientUser } = useUser();
  const role = useUserRole();
  const userId = useUserId();
  const { agreementId } = useParams();
  const { getByUserAndAgreement, signAgreement } = useAgreement();
  const [agreement, setAgreement] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getByUserAndAgreement(role, userId, agreementId)
      .then(setAgreement)
      .catch(console.error);
  }, [role, userId, agreementId]);

  if (!agreement) {
    return <div>Loading agreement…</div>;
  }
  const currentUser = role === "worker" ? workerUser : clientUser;
  if (!currentUser) {
    return <div>Loading user info…</div>;
  }

  const {
    messageId,
    clientId,
    clientName,
    createdBy,
    jobId,
    jobFrequency,
    sessions,
    hoursPerWeek,
    selectedJobStartDate,
    sessionStartDate,
    sessionEndDate,
    workerName,
    jobLocation,
    jobTitle,
    jobDescription,
    totalAgreementHours,
    rates,
    offeredRateTypes,
    preferredDays = {},
    timeSlots = {},
    noFeeForInitialGreetingSession,
    initialGreetingSessionRate,
    includeTravelRate,
    travelRatePerMile,
    editorContent,
    workerSigned,
    clientSigned,
    status,
    agreementDate,
  } = agreement;

  const cleanedJobDescription = cleanJobDescription(jobDescription);

  const handleEditAgreement = () => {
    navigate("/account/ps-dashboard/agreement-setup", {
      state: agreement,
    });
  };

  const handleSignAgreement = async () => {
    try {
      await signAgreement(role, agreementId);
      const messageText = `<strong>Agreement Signed!</strong> Looking forward to working with you.`;
      await axios.post(
        `${config.API_BASE_URL}/v1/messages/addChatMessage/${messageId}`,
        {
          text: messageText,
          timestamp: new Date().toISOString(),
          sender: role,
          receiver: role === "worker" ? "client" : "worker",
          jobId,
          read: false,
        }
      );
    } catch (err) {
      console.error("Error signing agreement:", err);
    }
  };

  const renderAdditionalConsiderations = () => {
    if (editorContent?.trim()) {
      return (
        <div className="agreement-travel">
          <h3>
            <FaFileAlt className="icon-common icon-file" />
            Additional Considerations
          </h3>
          <div
            className="agreement-review-editor-content"
            dangerouslySetInnerHTML={{ __html: editorContent }}
          />
        </div>
      );
    }
    return (
      <div className="agreement-travel">
        <h3>
          <FaFileAlt className="icon-common icon-file" />
          Additional Considerations
        </h3>
        <p>N/A</p>
      </div>
    );
  };

  return (
    <div className="agreement-view-component-container">
      <div className="agreement-job-details">
        <h3>
          <FaUserTie className="icon-common icon-user" />
          Parties to the Agreement
        </h3>
        <p>
          This agreement is entered into by <strong>{agreement.workerName}</strong>&nbsp;
          hereinafter referred to as <strong>Worker</strong> and
          <strong>{agreement.clientName}</strong>&nbsp; hereinafter referred to as
          <strong>Client</strong>.
        </p>

        <div className="agreement-travel">
          <h3>
            <LuClipboardList className="icon-common icon-list" />
            Job details
          </h3>

          <p>
            Title <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobTitle}</span>
            </strong>
          </p>
          <p>
            Location <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobLocation}</span>
            </strong>
          </p>
          <p>
            Job Frequency <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobFrequency}</span>
            </strong>
          </p>
          <p>
            Sessions / Week <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{sessions}</span>&nbsp;
            </strong>
          </p>
          <p>
            Hours / Session <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{hoursPerWeek}</span>&nbsp;
            </strong>
          </p>
          <p>
            Start Date <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{selectedJobStartDate}</span>
            </strong>
          </p>
        </div>
        <div className="agreement-travel">
          <h3><LuClipboardList className="icon-common icon-list" />
            Job Description</h3>

          <p>{cleanedJobDescription}</p>

        </div>
      </div>

      <div className="agreement-travel">
        <h3>
          <FaRegCalendarAlt className="icon-common icon-calendar" />
          Agreement Days &amp; Time Slots
        </h3>
        <div className="agreement-rates-display">
          {Object.entries(preferredDays)
            .filter(([, ok]) => ok)
            .map(([day]) => (
              <div key={day} className="rate-item">
                {day}
                <span className="user-dashboard-arrow">➤</span>
                {(timeSlots[day] || []).map((slot, i) => (
                  <strong>
                    <span key={i}>
                      {slot.from}–{slot.to}{i < timeSlots[day].length - 1 ? ", " : ""}
                    </span>
                  </strong>
                ))}
              </div>
            ))}

          <div className="rate-item">
            Total Agreement Hours<span className="user-dashboard-arrow">➤</span> <strong>{totalAgreementHours}</strong>
          </div>
        </div>

      </div>

      <div className="agreement-travel">

      </div>

      <div className="agreement-travel">
        <h3>
          <FaDollarSign className="icon-common icon-dollar" />
          Agreed Rates
        </h3>
        <div className="agreement-rates-display">
          {offeredRateTypes.map((type) => (
            <div key={type} className="rate-agreement-review-content">
              <div className="rate-item">
                {type}<span className="user-dashboard-arrow">➤</span><strong>${rates[type]}</strong>
              </div>
              <div className="net-rate-note">
                Net after 7% fee: $
                {(parseFloat(rates[type]) * 0.93).toFixed(2)}
              </div>
            </div>
          ))}
          <div className="rate-agreement-review-content">
            <div className="rate-item">
              Meet &amp; Greet rate<span className="user-dashboard-arrow">➤</span>
              {noFeeForInitialGreetingSession
                ? "$0"
                : `$${initialGreetingSessionRate}`}
            </div>
            {!noFeeForInitialGreetingSession && (
              <div className="net-rate-note">
                Net after 7% fee: $
                {(parseFloat(initialGreetingSessionRate) * 0.93).toFixed(2)}
              </div>
            )}
            {includeTravelRate && travelRatePerMile && (
              <div className="rate-item">
                Travel Rate/mile: ${travelRatePerMile}
              </div>
            )}</div>

        </div>
      </div>

      {renderAdditionalConsiderations()}
      <div className="agreement-job-details">
        <div className="agreement-travel">
          <h3>
            <FaFileAlt className="icon-common icon-file" />
            Reference Details
          </h3>
          <p>
            Agreement Ref<span className="user-dashboard-arrow">➤</span>
            <strong>{agreementId}</strong>
          </p>
          <p>
            Date and Time<span className="user-dashboard-arrow">➤</span>
            {agreementDate ? formatDateString(agreementDate) : "N/A"}
          </p>
          <p>
            Job Ref<span className="user-dashboard-arrow">➤</span>
            <strong>{jobId}</strong>
          </p>
          <p>
            Client Ref<span className="user-dashboard-arrow">➤</span>
            <strong>{clientId}</strong>
          </p>
          <p>
            Worker Ref<span className="user-dashboard-arrow">➤</span>
            <strong>{clientId}</strong>
          </p>
        </div>
      </div>

      <div className="agreement-header-edit-btn">
        {role === "worker" ? (
          <button
            className="agreement-edit-button"
            onClick={handleEditAgreement}
          >
            <FontAwesomeIcon icon={faEdit} /> Edit Agreement
          </button>
        ) : (
          <button
            className="agreement-review-button"
            onClick={handleSignAgreement}
          >
            Agree &amp; Submit
          </button>
        )}
      </div>
    </div>
  );
};

export default AgreementDetails;
